import type { DiagnosticsPane } from "./diagnostics-ui";
import type {
  AbsoluteElementPath,
  AbsoluteParentPath,
  NodeIdentifier,
} from "./project";
import * as project from "./project";
import type { SearchMenu } from "./search-menu";
import { type ProjectStore, SaveStatus } from "./store";

export class EditTree {
  readonly root: HTMLElement;
  readonly statusBar: StatusBar;

  private schema: SchemaElement;

  // TODO(Felix): Use interfaces in here to decouple from the actual dependencies
  // and simplify testing.
  // Or maybe use a set of callbacks instead (probably even better, since more local).
  constructor(
    private store: ProjectStore,
    searchMenu: SearchMenu,
    diagnosticsPane: DiagnosticsPane,
  ) {
    this.root = document.createElement("div");
    this.root.classList.add("card", "flex-grow-1");
    this.root.innerHTML = `
        <div class="card-body flex-grow-1 overflow-y-scroll" style="height: 0px;">
            <div class="container p-0">
            </div>
        </div>
    `;

    this.statusBar = new StatusBar(this.store, diagnosticsPane);
    this.root.appendChild(this.statusBar.root);

    const container = this.root.querySelector(".container")!;

    this.schema = new SchemaElement(store, searchMenu);
    container.append(this.schema.root);
  }

  public detach() {
    this.schema.detach();
    this.statusBar.detach();
  }
}

/**
 * Factory class to construct new field/group nodes.
 * The factory will remember each of the created nodes and propagate updates from
 * the store to the nodes.
 * This is an optimization to avoid adding event listeners to the store from
 * each node, which will be significant (around 600ms, e.g. 90% of the total
 * time used to construct the tree) when loading the BOB schema.
 * Our goal should be to work efficiently for all realistic workloads.
 */
/**
class NodeFactory {
 private groups: Map<string, GroupElement> = new Map();
 private fields: Map<string, FieldElement> = new Map();

 constructor(private store: ProjectStore) {}
}
*/

export class StatusBar {
  readonly root: HTMLElement;

  private saveStatus: HTMLElement;
  private diagnostics: HTMLElement;

  constructor(
    private store: ProjectStore,
    diagnosticsPane: DiagnosticsPane,
  ) {
    this.root = document.createElement("div");
    this.root.classList.add(
      "card-footer",
      "bg-light",
      "d-flex",
      "flex-row",
      "justify-content-between",
    );
    this.root.innerHTML = `
        <div id="save-status"></div>
        <a id="diagnostics" href="#" class="text-decoration-none link-dark">
            Diagnostics
        </a>
    `;

    this.saveStatus = this.root.querySelector("#save-status")!;

    this.diagnostics = this.root.querySelector("#diagnostics")!;
    this.diagnostics.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();

      diagnosticsPane.toggle();
    });

    this.store.addEventListener("diagnostics", this.updateDiagnostics);
    this.updateDiagnostics();

    this.store.addEventListener("saveStatus", this.updateSaveStatus);
    this.updateSaveStatus();
  }

  public detach() {
    this.store.removeEventListener("diagnostics", this.updateDiagnostics);
    this.store.removeEventListener("saveStatus", this.updateSaveStatus);
  }

  private updateSaveStatus = () => {
    const status = this.store.saveStatus;
    switch (status) {
      case SaveStatus.SAVING:
        {
          this.saveStatus.innerHTML = renderSaveStatus(
            "Wird gespeichert...",
            "text-light",
            "bi-cloud-upload",
          );
        }
        break;
      case SaveStatus.SAVED:
        {
          this.saveStatus.innerHTML = renderSaveStatus(
            "Gespeichert",
            "text-success",
            "bi-check-circle",
          );
        }
        break;
    }
  };

  private updateDiagnostics = () => {
    const diagnostics = this.store.diagnostics;

    if (diagnostics.length === 0) {
      this.diagnostics.innerHTML = `
                <small class="text-success fw-bold">Keine Probleme</small>
            `;
    } else {
      this.diagnostics.innerHTML = `
                <small class="fw-bold text-danger">
                    <i class="bi bi-exclamation-diamond-fill"></i>
                    ${diagnostics.length} Problem(e)
                </small>
            `;
    }
  };
}

function renderSaveStatus(label: string, color: string, icon: string): string {
  return `
        <small class="${color} fw-bold">
            <i class="bi ${icon} me-1"></i>${label}
        </small>
    `;
}

class SchemaElement {
  readonly root: HTMLElement;
  private elementPath: AbsoluteElementPath;

  private collapseLink: HTMLLinkElement;
  private collapseIcon: HTMLElement;
  private childContainer: ChildContainer;

  private readonly node: Node;

  private schema: project.Schema;

  constructor(
    private store: ProjectStore,
    private searchMenu: SearchMenu,
  ) {
    this.schema = this.store.schema;
    this.elementPath = [{ type: "schema", fimId: this.schema.fimId }];

    this.root = document.createElement("div");
    this.root.innerHTML = `
        <div id="node" class="d-flex flex-row align-items-center gap-1">
            <a id="collapse" class="edit-tree-toggle text-decoration-none" href="#">
                <i class="bi bi-caret-down"></i>
            </a>
        </div>
        `;

    this.node = new Node(
      store,
      this.elementPath,
      this.store.schema.type === "version"
        ? this.store.schema.fimVersion
        : undefined,
    );
    this.root.querySelector("#node")!.appendChild(this.node.root);

    this.childContainer = new ChildContainer(
      this.elementPath,
      this.store,
      this.schema.type === "version",
      this.searchMenu,
      () => this.searchMenu.open({ type: "schema", fimId: this.schema.fimId }),
    );
    this.root.appendChild(this.childContainer.root);

    this.collapseLink = this.root.querySelector("#collapse")!;
    this.collapseIcon = this.collapseLink.children[0] as HTMLElement;
    this.collapseLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.toggleChildren();
    });

    this.store.addEventListener("schema", this.onUpdate);

    this.update(this.schema);
  }

  public detach() {
    this.childContainer.detachAll();
    this.node.detach();
    this.store.removeEventListener("schema", this.onUpdate);
  }

  private toggleChildren() {
    if (this.collapseIcon.classList.contains("bi-caret-down")) {
      this.hideChildren();
    } else {
      this.showChildren();
    }
  }

  private showChildren() {
    this.collapseLink.children[0].classList.remove("bi-caret-right-fill");
    this.collapseLink.children[0].classList.add("bi-caret-down");

    this.childContainer.showChildren();
  }

  private hideChildren() {
    this.collapseLink.children[0].classList.add("bi-caret-right-fill");
    this.collapseLink.children[0].classList.remove("bi-caret-down");

    this.childContainer.hideChildren();
  }

  private onUpdate = () => {
    const schema = this.store.schema;
    this.update(schema);
  };

  private update(schema: project.Schema) {
    this.schema = schema;

    this.childContainer.update(schema.children);

    this.showChildren();
  }
}

class ChildContainer {
  readonly root: HTMLElement;

  private childContainer: HTMLElement;
  private childElements: Map<string, BuilderElement>;

  constructor(
    private parentPath: AbsoluteParentPath,
    private store: ProjectStore,
    private readOnly: boolean,
    private searchMenu: SearchMenu,
    onAddChild: () => void,
  ) {
    this.root = document.createElement("div");
    this.root.classList.add("edit-tree-toggle-padding");
    this.root.innerHTML = `
            <div id="child-container"></div>
            <div class="d-flex flex-row gap-1">
                <span class="edit-tree-toggle"></span>
                <button id="add-child" class="btn btn-light mt-2">
                    <i class="bi bi-plus"></i> Element hinzufügen
                </button>
            </div>
        `;

    this.childContainer = this.root.querySelector(
      "#child-container",
    ) as HTMLElement;

    this.childElements = new Map();

    const addChild = this.root.querySelector("#add-child") as HTMLButtonElement;

    if (readOnly) {
      addChild.remove();
    } else {
      addChild.addEventListener("click", () => {
        project.assert(!this.readOnly);
        onAddChild();
      });
    }
  }

  public update(children: project.Child[]) {
    // Remove all children not longer included
    const newIds = new Set(children.map((child) => child.element.fimId));
    const existingIds = Array.from(this.childElements.keys());
    for (const id of existingIds) {
      if (!newIds.has(id)) {
        const element = this.childElements.get(id)!;

        this.detachElement(element);
        this.childElements.delete(id);
      }
    }

    // Update existing elements, create new ones.
    // Add elements to the UI.
    const childHTMLElements = [];
    for (const child of children) {
      let element = this.childElements.get(child.element.fimId);

      if (element === undefined) {
        element = this.createElement(child);
        this.childElements.set(child.element.fimId, element);
      }

      element.updateIsParentReadonly(this.readOnly);

      childHTMLElements.push(element.root);
    }

    // Replace all the children in the DOM.
    this.childContainer.replaceChildren(...childHTMLElements);

    project.assert(
      this.childContainer.children.length === this.childElements.size,
    );
  }

  public detachAll() {
    for (const element of this.childElements.values()) {
      element.detach();
    }
  }

  private createElement(child: project.Child): BuilderElement {
    if (this.readOnly) {
      project.assert(
        child.element.fimVersion !== undefined,
        "A versioned element cannot contain an Arbeitskopie.",
      );
    }

    switch (child.type) {
      case "group": {
        return new GroupElement(
          this.parentPath,
          child.element,
          this.store,
          this.searchMenu,
        );
      }

      case "field": {
        return new FieldElement(this.parentPath, child.element, this.store);
      }
    }
  }

  private detachElement(element: BuilderElement) {
    element.detach();
  }

  public showChildren() {
    this.root.classList.remove("d-none");
  }

  public hideChildren() {
    this.root.classList.add("d-none");
  }
}

type BuilderElement = GroupElement | FieldElement;

/**
 * The menu with the "Up", "Down" and "Delete" buttons to edit the schema tree structure.
 */
class TreeChildMenu {
  readonly root: HTMLElement;

  private upButton: HTMLButtonElement;
  private downButton: HTMLButtonElement;
  private deleteButton: HTMLButtonElement;

  constructor(
    store: ProjectStore,
    parentIdentifier: project.ParentIdentifier,
    childIdentifier: project.ChildIdentifier,
  ) {
    this.root = document.createElement("div");
    this.root.classList.add("d-flex", "flex-row", "gap-1");

    this.upButton = document.createElement("button");
    this.upButton.classList.add("btn", "btn-light");
    this.upButton.innerHTML = `<i class="bi bi-arrow-up"></i>`;
    this.upButton.addEventListener("click", () => {
      store.moveChildUp(parentIdentifier, childIdentifier);
    });
    this.root.appendChild(this.upButton);

    this.downButton = document.createElement("button");
    this.downButton.classList.add("btn", "btn-light");
    this.downButton.innerHTML = `<i class="bi bi-arrow-down"></i>`;
    this.downButton.addEventListener("click", () => {
      store.moveChildDown(parentIdentifier, childIdentifier);
    });
    this.root.appendChild(this.downButton);

    this.deleteButton = document.createElement("button");
    this.deleteButton.classList.add("btn", "btn-light");
    this.deleteButton.innerHTML = `<i class="bi bi-trash"></i>`;
    this.deleteButton.addEventListener("click", () => {
      store.removeChild(parentIdentifier, childIdentifier);
    });
    this.root.appendChild(this.deleteButton);
  }

  public updateIsParentReadonly(isParentReadonly: boolean) {
    if (isParentReadonly) {
      this.upButton.disabled = true;
      this.downButton.disabled = true;
      this.deleteButton.disabled = true;

      this.upButton.classList.add("text-secondary");
      this.downButton.classList.add("text-secondary");
      this.deleteButton.classList.add("text-secondary");
    } else {
      this.upButton.disabled = false;
      this.downButton.disabled = false;
      this.deleteButton.disabled = false;

      this.upButton.classList.remove("text-secondary");
      this.downButton.classList.remove("text-secondary");
      this.deleteButton.classList.remove("text-secondary");
    }
  }
}

class FieldElement {
  readonly root: HTMLElement;

  private readonly node: Node;
  private readonly menu: TreeChildMenu;

  constructor(
    parentPath: AbsoluteParentPath,
    identifier: project.Identifier,
    store: ProjectStore,
  ) {
    this.root = document.createElement("div");
    this.root.classList.add(
      "pt-2",
      "d-flex",
      "flex-row",
      "align-items-center",
      "gap-1",
    );
    this.root.innerHTML = `
        <span class="edit-tree-toggle"></span>
    `;

    const elementIdentifier: project.FieldIdentifier = {
      type: "field",
      fimId: identifier.fimId,
    };
    const elementPath: AbsoluteElementPath = [...parentPath, elementIdentifier];
    this.node = new Node(store, elementPath, identifier.fimVersion);
    this.root.appendChild(this.node.root);

    this.menu = new TreeChildMenu(
      store,
      parentPath[parentPath.length - 1],
      elementIdentifier,
    );
    this.root.appendChild(this.menu.root);
  }

  public updateIsParentReadonly(isParentReadonly: boolean) {
    this.menu.updateIsParentReadonly(isParentReadonly);
  }

  public detach() {
    this.node.detach();
  }
}

class GroupElement {
  readonly root: HTMLElement;
  private elementPath: AbsoluteParentPath;

  private collapseLink: HTMLLinkElement;
  private collapseIcon: HTMLElement;

  private node: Node;
  private menu: TreeChildMenu;
  private childContainer: ChildContainer;

  constructor(
    parentPath: AbsoluteParentPath,
    private identifier: project.Identifier,
    private store: ProjectStore,
    private searchMenu: SearchMenu,
  ) {
    const elementIdentifier: project.GroupIdentifier = {
      type: "group",
      fimId: identifier.fimId,
    };
    this.elementPath = [...parentPath, elementIdentifier];

    this.root = document.createElement("div");
    this.root.classList.add("pt-2");
    this.root.innerHTML = `
        <div id="node" class="d-flex flex-row align-items-center gap-1">
            <a id="collapse" class="edit-tree-toggle text-decoration-none" href="#">
                <i class="bi bi-caret-down"></i>
            </a>
        </div>
        `;

    this.node = new Node(store, this.elementPath, identifier.fimVersion);
    this.menu = new TreeChildMenu(
      store,
      parentPath[parentPath.length - 1],
      elementIdentifier,
    );
    this.root.querySelector("#node")!.append(this.node.root, this.menu.root);

    this.childContainer = new ChildContainer(
      this.elementPath,
      store,
      identifier.fimVersion !== undefined,
      this.searchMenu,
      () => {
        project.assert(
          identifier.fimVersion === undefined,
          "Cannot add children to a versioned group.",
        );
        this.searchMenu.open(elementIdentifier);
      },
    );

    this.root.appendChild(this.childContainer.root);

    this.collapseLink = this.root.querySelector("#collapse")!;
    this.collapseIcon = this.collapseLink.children[0] as HTMLElement;
    this.collapseLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.toggleChildren();
    });

    this.store.addEventListener(this.identifier.fimId, this.onUpdate);
    this.onUpdate();
  }

  public updateIsParentReadonly(isParentReadonly: boolean) {
    this.menu.updateIsParentReadonly(isParentReadonly);
  }

  private onUpdate = () => {
    const group = this.store.groups[this.identifier.fimId];
    this.update(group);
  };

  public detach() {
    this.childContainer.detachAll();
    this.node.detach();
    this.store.removeEventListener(this.identifier.fimId, this.onUpdate);
  }

  public update(group: project.Group) {
    this.childContainer.update(group.children);
  }

  private toggleChildren() {
    if (this.collapseIcon.classList.contains("bi-caret-down")) {
      this.hideChildren();
    } else {
      this.showChildren();
    }
  }

  private showChildren() {
    this.collapseLink.children[0].classList.remove("bi-caret-right-fill");
    this.collapseLink.children[0].classList.add("bi-caret-down");

    this.childContainer.showChildren();
  }

  private hideChildren() {
    this.collapseLink.children[0].classList.add("bi-caret-right-fill");
    this.collapseLink.children[0].classList.remove("bi-caret-down");

    this.childContainer.hideChildren();
  }
}

class Node {
  private readonly identifier: NodeIdentifier;

  readonly root: HTMLElement;
  private readonly card: HTMLElement;
  private readonly header: HTMLElement;
  private readonly idSpan: HTMLElement;

  constructor(
    private store: ProjectStore,
    path: AbsoluteElementPath,
    fimVersion: string | undefined,
  ) {
    this.identifier = path[path.length - 1];

    this.root = document.createElement("a");
    this.root.classList.add("text-decoration-none", "link-dark", "flex-grow-1");
    this.root.setAttribute("href", "#");
    this.root.innerHTML = `
        <div class="card card-link">
            <div class="card-body p-2">
                <div class="d-flex flex-row gap-1 justify-content-between align-items-center">
                    <div class="d-flex flex-row gap-1 align-items-center">
                        <i class="bi ${getNodeIcon(this.identifier.type, fimVersion)}"></i>
                        <h6 class="m-0 flex-wrap"></h6>
                    </div>
                    <small class="text-secondary"></small>
                </div>
            </div>
        </div>
        <div id="controls">
        </div>
    `;

    this.card = this.root.querySelector(".card")!;
    this.header = this.root.querySelector("h6")!;
    this.idSpan = this.root.querySelector("small")!;

    if (fimVersion !== undefined) {
      this.card.classList.add("text-bg-light");
    }

    this.idSpan.textContent = this.identifier.fimId;

    this.store.addEventListener("active", this.onActive);
    this.store.addEventListener(this.identifier.fimId, this.onUpdate);

    this.root.addEventListener("click", (event) => {
      event.preventDefault();
      store.selectElement(path);
    });

    this.onActive();
    this.onUpdate();
  }

  public detach() {
    this.store.removeEventListener("active", this.onActive);
    this.store.removeEventListener(this.identifier.fimId, this.onUpdate);
  }

  private onUpdate = () => {
    switch (this.identifier.type) {
      case "schema":
        {
          const schema = this.store.schema;
          this.setName(schema.basicData.name);
        }
        break;
      case "group":
        {
          const group = this.store.groups[this.identifier.fimId];
          this.setName(group.basicData.name);
        }
        break;
      case "field":
        {
          const field = this.store.fields[this.identifier.fimId];
          this.setName(field.basicData.name);
        }
        break;
    }
  };

  private onActive = () => {
    if (this.store.activeElement.fimId === this.identifier.fimId) {
      this.card.classList.add("border-primary", "shadow");
    } else {
      this.card.classList.remove("border-primary", "shadow");
    }
  };

  private setName(name: string) {
    this.header.textContent = name;
  }
}

function getNodeIcon(
  type: "schema" | "group" | "field",
  fimVersion: string | undefined,
): string {
  if (fimVersion !== undefined) {
    return "bi bi-link";
  }

  switch (type) {
    case "schema":
      return "bi-file-earmark";
    case "group":
      return "bi-collection";
    case "field":
      return "bi-input-cursor";
  }
}
